import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import useDebounce from '../../../hooks/useDebounce';
import PageHeader from '../../../components/PageHeader';
import { usePermissions } from '../../../hooks/usePermissions';
import RetentionTable from '../components/RetentionTable';
import RetentionModalForm from '../components/RetentionModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import retentionService from '../services/retentionService';
import { companyService } from '../../companies';
import { areaService } from '../../areas';

const RetentionList = () => {
  const { hasPermission } = usePermissions();
  const [retentions, setRetentions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedRetention, setSelectedRetention] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadAreas(selectedCompany);
    } else {
      setAreas([]);
      setSelectedArea('');
    }
  }, [selectedCompany]);

  useEffect(() => {
    loadRetentions();
  }, [page, debouncedSearch, selectedCompany, selectedArea, sortBy, sortDir]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadAreas = async (companyId) => {
    try {
      const response = await areaService.getAll({ companyId, limit: 500 });
      setAreas(response.data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
    }
  };

  const loadRetentions = async () => {
    try {
      setLoading(true);
      const response = await retentionService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        areaId: selectedArea,
        page,
        limit: 10,
        sortBy,
        sortDir,
      });
      setRetentions(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar tablas de retención', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedRetention(null);
    setOpenModal(true);
  };

  const handleEdit = (retention) => {
    setSelectedRetention(retention);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta tabla de retención?')) {
      try {
        await retentionService.delete(id);
        setRetentions(prev => prev.filter(r => r.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Tabla de retención eliminada exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar tabla de retención', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedRetention) {
        await retentionService.update(selectedRetention.id, data);
        setRetentions(prev => prev.map(r =>
          r.id === selectedRetention.id ? { ...r, ...data } : r
        ));
        showSnackbar('Tabla de retención actualizada exitosamente', 'success');
      } else {
        await retentionService.create(data);
        showSnackbar('Tabla de retención creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadRetentions();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar tabla de retención', 'error');
      throw error;
    }
  };

  const handleSortChange = (field) => {
    if (field === sortBy) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <PageHeader
        title="Tablas de Retención"
        subtitle="TRD por empresa y area: series, subseries y disposicion final."
        stat={pagination?.total || 0}
        statLabel="Tablas"
        actions={hasPermission('retention.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Tabla
          </Button>
        )}
      />

      <Box sx={{ bgcolor: '#f2f2f3', p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre o código..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Empresa</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setPage(1);
                }}
                displayEmpty
              >
                <MenuItem value="">Todas</MenuItem>
                {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Área</Typography>
            <Autocomplete
              options={areas.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
              getOptionLabel={(o) => o.name || ''}
              value={areas.find(a => String(a.id) === String(selectedArea)) || null}
              onChange={(_, val) => {
                setSelectedArea(val ? val.id : '');
                setPage(1);
              }}
              disabled={!selectedCompany}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              noOptionsText="Sin áreas disponibles"
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Área" />
              )}
            />
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <RetentionTable
          retentions={retentions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canEdit={hasPermission('retention.update')}
          canDelete={hasPermission('retention.delete')}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />
      )}

      <RetentionModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        retention={selectedRetention}
        companies={companies}
        areas={areas}
        onCompanyChange={loadAreas}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetentionList;
